import { useCallback, useEffect, useMemo, useState } from "react"
import If from "../../../if"
import ProgressBar from "../../../progress-bar"
import { FourColumnPerformanceSkeleton } from "../../../skeletons"
import PillTabs from "../../../pill-tabs"
import NoData from "../../../no-data"
import DateFilter, { DateRangePreset } from "../../../activity/date-filter"
import {
  useFetchTopLeadBreakdownTypeEmbedsQuery,
  useFetchTopLeadBreakdownTypeLOsQuery,
  useFetchTopLeadBreakdownTypeURLQuery
} from "../../../../../redux/queries"
import {
  TransformTopLeadBreakdown,
  useTransformTopLeadBreakdown
} from "../../../../hooks"
import { TopLeadBreakdownData } from "../../../../../types"
import { cleanUrl } from "../../../../../common/utils/string"
import { DateTime } from "luxon"

export interface TopLeadBreakdownFilter {
  type: "url" | "embed"
  value: string
}

interface TopLeadBreakdownProps {
  title: string
  range: number
  orgId: string | undefined
  loid: string | undefined
  hasRangeFilter?: boolean
  contentHeight?: string
  className?: string
  hideRangeFilterLabelOnMobile?: boolean
  onSelectFilter?: (filter: TopLeadBreakdownFilter | null) => void
  activeFilter?: TopLeadBreakdownFilter | null
}

export enum TopLeadBreakdownTabs {
  URL = "breakdown-by-url",
  EMBEDS = "breakdown-by-embeds",
  LOS = "breakdown-by-los"
}

enum TopLeadBreakdownLabelTabs {
  URL = "URL",
  EMBEDS = "Embeds",
  LOS = "LO's"
}

const TopLeadBreakdown = ({
  title,
  range,
  orgId,
  loid,
  hasRangeFilter = false,
  contentHeight = "330px",
  className = "",
  hideRangeFilterLabelOnMobile = false,
  onSelectFilter,
  activeFilter
}: TopLeadBreakdownProps) => {
  const [isFetching, setIsFetching] = useState<boolean>(true)
  const [tab, setTab] = useState<string>(TopLeadBreakdownTabs.URL)
  const [transformedData, setTransformedData] = useState<
    TransformTopLeadBreakdown[]
  >([])
  const [dateRange, setDateRange] = useState<DateRangePreset>(
    range === 90 ? "last_90" : range === 30 ? "last_30" : "last_7"
  )
  const [customStartDate, setCustomStartDate] = useState<Date | null>(null)
  const [customEndDate, setCustomEndDate] = useState<Date | null>(null)

  // Convert DateRangePreset to numeric days for API
  const days = useMemo(() => {
    if (!hasRangeFilter) return range
    switch (dateRange) {
      case "today":
        return 1
      case "last_7":
        return 7
      case "last_30":
        return 30
      case "last_90":
        return 90
      case "custom":
        if (customStartDate && customEndDate) {
          const start = DateTime.fromJSDate(customStartDate)
          const end = DateTime.fromJSDate(customEndDate)
          return Math.ceil(end.diff(start, "days").days)
        }
        return 30
      case "all":
      default:
        return 1000
    }
  }, [hasRangeFilter, range, dateRange, customStartDate, customEndDate])

  const previousRange = days * 2

  const handleDateRangeChange = useCallback(
    (range: DateRangePreset, startDate?: Date, endDate?: Date) => {
      setDateRange(range)
      setCustomStartDate(startDate || null)
      setCustomEndDate(endDate || null)
    },
    []
  )

  const fetchParams = (range: number) => ({
    range: range,
    org_id: orgId,
    loid: loid
  })
  const fetchOptions = (canSkip: boolean) => ({
    refetchOnMountOrArgChange: true,
    skip: canSkip
  })

  const { data: urlData, isFetching: isFetchingUrl } =
    useFetchTopLeadBreakdownTypeURLQuery(
      fetchParams(days),
      fetchOptions(tab !== TopLeadBreakdownTabs.URL)
    )
  const { data: urlPreviousData, isFetching: isFetchingUrlPrevious } =
    useFetchTopLeadBreakdownTypeURLQuery(
      fetchParams(previousRange),
      fetchOptions(tab !== TopLeadBreakdownTabs.URL)
    )
  const { data: embedsData, isFetching: isFetchingEmbeds } =
    useFetchTopLeadBreakdownTypeEmbedsQuery(
      fetchParams(days),
      fetchOptions(tab !== TopLeadBreakdownTabs.EMBEDS)
    )
  const { data: embedsPreviousData, isFetching: isFetchingEmbedsPrevious } =
    useFetchTopLeadBreakdownTypeEmbedsQuery(
      fetchParams(previousRange),
      fetchOptions(tab !== TopLeadBreakdownTabs.EMBEDS)
    )
  const { data: losData, isFetching: isFetchingLOs } =
    useFetchTopLeadBreakdownTypeLOsQuery(
      fetchParams(days),
      fetchOptions(tab !== TopLeadBreakdownTabs.LOS)
    )
  const { data: losPreviousData, isFetching: isFetchingLOsPrevious } =
    useFetchTopLeadBreakdownTypeLOsQuery(
      fetchParams(previousRange),
      fetchOptions(tab !== TopLeadBreakdownTabs.LOS)
    )

  const breakdownData: TopLeadBreakdownData[] | undefined = useMemo(() => {
    if (!isFetching) {
      switch (tab) {
        case TopLeadBreakdownTabs.URL:
          return urlData
        case TopLeadBreakdownTabs.EMBEDS:
          return embedsData
        case TopLeadBreakdownTabs.LOS:
          return losData
        default:
          return []
      }
    }
  }, [urlData, embedsData, losData, isFetching, tab])
  const previousBreakdownData: TopLeadBreakdownData[] | undefined =
    useMemo(() => {
      if (!isFetching) {
        switch (tab) {
          case TopLeadBreakdownTabs.URL:
            return urlPreviousData
          case TopLeadBreakdownTabs.EMBEDS:
            return embedsPreviousData
          case TopLeadBreakdownTabs.LOS:
            return losPreviousData
          default:
            return []
        }
      }
    }, [isFetching, tab, urlPreviousData, embedsPreviousData, losPreviousData])
  const maxCount = useMemo(
    () =>
      transformedData?.length > 0
        ? Math.max(...transformedData.map(data => data.lead_count))
        : 0,
    [transformedData]
  )
  const currentTransformedData = useTransformTopLeadBreakdown(
    tab,
    breakdownData,
    previousBreakdownData
  )

  useEffect(() => {
    setTransformedData(currentTransformedData)
  }, [currentTransformedData])

  useEffect(() => {
    setIsFetching(
      isFetchingUrl ||
        isFetchingEmbeds ||
        isFetchingLOs ||
        isFetchingUrlPrevious ||
        isFetchingEmbedsPrevious ||
        isFetchingLOsPrevious
    )
  }, [
    isFetchingEmbeds,
    isFetchingEmbedsPrevious,
    isFetchingLOs,
    isFetchingLOsPrevious,
    isFetchingUrl,
    isFetchingUrlPrevious
  ])

  return (
    <div
      className={`bg-white flex flex-col flex-1 px-6 pt-4 pb-7 rounded-md overflow-hidden ${className}`}
    >
      <div className="flex gap-4 lg:gap-0 content-center items-center justify-between flex-wrap">
        <h3 className="text-base font-medium">{title}</h3>

        {hasRangeFilter ? (
          <div className="flex lg:justify-end lg:ml-4">
            <DateFilter
              selectedRange={dateRange}
              customStartDate={customStartDate}
              customEndDate={customEndDate}
              onRangeChange={handleDateRangeChange}
              hideLabelOnMobile={hideRangeFilterLabelOnMobile}
            />
          </div>
        ) : (
          <PillTabs
            currentTab={tab}
            className={`${hasRangeFilter ? "mt-6 mx-auto" : "mx-auto md:mx-0"}`}
            tabs={[
              {
                id: TopLeadBreakdownTabs.URL,
                label: TopLeadBreakdownLabelTabs.URL
              },
              {
                id: TopLeadBreakdownTabs.EMBEDS,
                label: TopLeadBreakdownLabelTabs.EMBEDS
              },
              {
                id: TopLeadBreakdownTabs.LOS,
                label: TopLeadBreakdownLabelTabs.LOS
              }
            ]}
            onChange={setTab}
          />
        )}
      </div>
      {hasRangeFilter ? (
        <PillTabs
          currentTab={tab}
          className={`${hasRangeFilter ? "mt-6 mx-auto" : ""}`}
          tabs={[
            {
              id: TopLeadBreakdownTabs.URL,
              label: TopLeadBreakdownLabelTabs.URL
            },
            {
              id: TopLeadBreakdownTabs.EMBEDS,
              label: TopLeadBreakdownLabelTabs.EMBEDS
            },
            {
              id: TopLeadBreakdownTabs.LOS,
              label: TopLeadBreakdownLabelTabs.LOS
            }
          ]}
          onChange={setTab}
        />
      ) : null}

      <If
        condition={isFetching}
        then={<FourColumnPerformanceSkeleton className="mt-px pt-11" />}
        else={
          <If
            condition={!transformedData || transformedData.length === 0}
            then={<NoData />}
            else={
              <div
                className="mt-7 overflow-y-auto scroll-smooth"
                style={{ height: contentHeight }}
              >
                <div className="w-full grid gap-y-6 py-2">
                  {transformedData.map((breakdownData, index) => {
                    const isUrlTab = tab === TopLeadBreakdownTabs.URL
                    const isEmbedsTab = tab === TopLeadBreakdownTabs.EMBEDS
                    const isClickable =
                      onSelectFilter && (isUrlTab || isEmbedsTab)
                    const value = isUrlTab
                      ? breakdownData.url
                      : breakdownData.name
                    const filterType = isUrlTab ? "url" : "embed"
                    const isActive =
                      activeFilter?.type === filterType &&
                      activeFilter?.value === value

                    return (
                      <ProgressBar
                        key={index}
                        count={breakdownData.lead_count}
                        maxCount={maxCount}
                        label={
                          isUrlTab
                            ? cleanUrl(breakdownData.url)
                            : breakdownData.name
                        }
                        netPercentage={breakdownData.netPercentage}
                        isPositive={breakdownData.isPositive}
                        isActive={isActive}
                        onClick={
                          isClickable && value
                            ? () =>
                                onSelectFilter(
                                  isActive ? null : { type: filterType, value }
                                )
                            : undefined
                        }
                      />
                    )
                  })}
                </div>
              </div>
            }
          />
        }
      />
    </div>
  )
}

export default TopLeadBreakdown
